import {
  DesignationHeaderCell,
  ProductComparisonHeaderCell,
  TechValHeaderCell,
  WeightHeaderCell,
} from "./ProductTabularViewHeaderCell";
import { type TabularColumnConfig, type TabularViewData } from "../../../../models";
import { ProductTabularViewActionCell } from "./ProductTabularViewActionCell/ProductTabularViewActionCell";
import { MAX_COLUMNS_IN_TABULAR_VIEW, NBSP, NON_BREAKING_HYPHEN } from "../../../../../utils/constants";
import { PopperTooltip } from "../../../../../common/components/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { selectProductComparisonList } from "../../../../../ProductComparison/redux/productComparisonSelectors";
import { userIsLoggedIn } from "../../../../../utils/roles";
import { addToComparison } from "../../../../../ProductComparison/redux/actions";
import { redirectToUrl } from "../../../../../utils/url/urlUtils";
import Checkbox from "../../../../../common/components/Checkbox";
import ProductDesignation from "../../../../../ProductDetails/components/ProductDesignation";
import { Link, LinkType } from "../../../../../common/components/Link";
import ProductPreferredIndicator from "../../../../../ProductDetails/components/ProductPreferredIndicator";
import { getProductComparisonNavigationEntryUrl } from "../../../../../ProductComparison/productComparisonHelper";
import { type ColumnDef } from "@tanstack/react-table";
import { HousingProductTabularViewActionCell } from "~/HousingSelectionAssistant/components/HousingTabularView/HousingProductTabularViewActionCell";
import { useUnitMode } from "~/ImperialMetricData/useUnitMode";
import { UnitMode } from "~/ImperialMetricData/redux/UnitMode";
import { getDesignationAbbreviation } from "~/common/designationAbbreviation";
import { useTranslation } from "react-i18next";
import { type ProductData } from "~/models/ProductData";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { getProductCampaignStatus } from "~/SalesCampaign/utils";

import { isNonNull } from "~/utils/reducers";
import ImperialFraction from "~/common/components/ImperialFraction";

interface RenderDesignationCellProps {
  product: ProductData;
}

export const renderDesignationCell =
  (canClick?: boolean, maxChars?: number) =>
  ({ product: data }: RenderDesignationCellProps) => {
    const abbreviatedDesignation = getDesignationAbbreviation(maxChars)(data.designation);
    const isAbbreviation = abbreviatedDesignation !== data.designation;
    const { hasSale, isSoldOut } = getProductCampaignStatus(data.salesCampaign);
    let designation = (
      <ProductDesignation
        designation={abbreviatedDesignation}
        size={"label-medium"}
        className={isAbbreviation ? "f-no-wrap" : undefined}
        title={isAbbreviation ? data.designation : undefined}
        noCssColor={true}
      />
    );

    if (canClick && data.url) {
      designation = (
        <Link href={data.url} variant={LinkType.NAVIGATION} target={"_blank"}>
          {designation}
        </Link>
      );
    }

    return (
      <div className={"d-flex"}>
        <ProductPreferredIndicator
          className={`icon-margin ${!data.preferred ? "v-hidden" : ""} scha__tabular-view__preferred-indicator`}
          side={"right"}
        />
        {designation}

        {hasSale && <SaleTag variant={isSoldOut ? "inactive" : "active"} size={20} className={"ml-auto flex-none"} />}
      </div>
    );
  };
export const renderNestedHSADesignationCell =
  (canClick?: boolean) =>
  ({ product: data }: RenderDesignationCellProps) => {
    const designationLabel = data.designation.replaceAll(" +", `${NBSP}+`).replaceAll("-", NON_BREAKING_HYPHEN);
    let designation = <ProductDesignation designation={designationLabel} size={"label-medium"} noCssColor={true} />;

    if (canClick && data.url) {
      designation = (
        <Link
          href={data.url}
          variant={LinkType.NAVIGATION}
          target={"_blank"}
          data-ta-spec={"housing_assistant_product"}
          data-ta-title={designationLabel}
        >
          {designation}
        </Link>
      );
    }

    return (
      <div className={"d-flex"}>
        <ProductPreferredIndicator
          className={`icon-margin ${!data.preferred ? "v-hidden" : ""} scha__tabular-view__preferred-indicator`}
          side={"right"}
        />
        {designation}
      </div>
    );
  };

export const tsmakeProductColumn = (isDesktop: boolean): ColumnDef<TabularViewData> => {
  return {
    id: "name",
    header: () => <DesignationHeaderCell />,
    size: isDesktop ? 180 : 138,
    cell: (cell) => renderDesignationCell(true, 120)(cell.row.original),
  };
};
export const tsmakeHSAProductColumn = (isDesktop: boolean): ColumnDef<TabularViewData> => {
  return {
    id: "name",
    header: () => <DesignationHeaderCell />,
    size: isDesktop ? 240 : 120,
    cell: (cell) => renderDesignationCell(cell.row.original.variantProducts.length === 0, 12)(cell.row.original),
    // cellStyle: {
    //   paddingLeft: 0,
    //   paddingRight: 10,
    // },
    // sorting: true,
  };
};

export const dataColSizeFn = (isDesktop: boolean, maxColumnAmount: number) => (id: string) => {
  if (!isDesktop) {
    return 85;
  }
  if (maxColumnAmount <= MAX_COLUMNS_IN_TABULAR_VIEW) {
    return undefined;
  }
  return ID_SIZE_MAP[id] ?? 105;
};

const ID_SIZE_MAP = {
  limitingspeedgrease: 160,
  limitingspeedoil: 135,
  limitingspeed: 125,
  referencespeed: 125,
  tempmin: 110,
  tempmax: 110,
};

const renderTechValueCell = (col: TabularColumnConfig) => (data: TabularViewData) => {
  const { abbreviation, abbreviationAddition, value = "-", isConverted } = data.techValues[col.techValueUID] ?? {};

  const isInch = col.unit?.toLowerCase() == "in";

  let label;
  if (window.SCHA.feature.inchToFraction && isInch) {
    label = <ImperialFraction value={value} />;
  } else {
    label = <span>{value}</span>;
  }

  if (abbreviation) {
    const abbreviationCmp = (
      <>
        <span>
          {abbreviation}
          {abbreviationAddition && <sub>{abbreviationAddition}</sub>}
        </span>{" "}
        {col.unit}
      </>
    );

    return (
      <PopperTooltip
        tooltipContent={<span className={"f-no-wrap"}>{abbreviationCmp}</span>}
        className={isConverted ? "d-inline f-info-color" : "d-inline"}
      >
        {label}
      </PopperTooltip>
    );
  }

  return label;
};
export const ComparisonCell = ({ data }: { data: TabularViewData }) => {
  const dispatch = useDispatch();
  const productCodes = useSelector(selectProductComparisonList);
  const isChecked = productCodes.some((code) => code === data.product.code);
  const loggedIn = userIsLoggedIn();
  const handleAddToComparison = () => {
    if (loggedIn) {
      return dispatch(addToComparison(data.product.code));
    }
    return redirectToUrl(getProductComparisonNavigationEntryUrl());
  };
  return <Checkbox onCheck={handleAddToComparison} checked={isChecked} className={"scha__comparison__tabular "} />;
};
export const WeightCell = ({ data }: { data: TabularViewData }) => {
  const { t } = useTranslation();
  const unitMode = useUnitMode();
  const valueWeight = unitMode === UnitMode.Metric ? data.weight : data.weightImperial;

  const label = <span>{valueWeight}</span>;

  const abbreviationCmp =
    unitMode === UnitMode.Metric
      ? t("page.selectionAssistant.tableHeader.mass.label")
      : t("page.selectionAssistant.tableHeader.mass.imperial.label");

  return (
    <PopperTooltip
      tooltipContent={<span className={"f-no-wrap"}>{abbreviationCmp}</span>}
      className={unitMode === UnitMode.Imperial && valueWeight !== "-" ? "d-inline f-info-color" : "d-inline"}
    >
      {label}
    </PopperTooltip>
  );
};
export const tsmakeTechValueColumnFactory =
  (sizeCalculator: (code: string) => number | undefined) =>
  (colDef: TabularColumnConfig): ColumnDef<TabularViewData> => {
    return {
      id: colDef.techValueUID,
      header: () => <TechValHeaderCell config={colDef} alignment={"right"} />,
      size: sizeCalculator(colDef.techValueUID),
      cell: (cell) => renderTechValueCell(colDef)(cell.row.original),
      meta: {
        align: "right",
      },
    };
  };

export const tsmakeWeightColumn = (
  sizeCalculator: (code: string) => number | undefined,
): ColumnDef<TabularViewData> => {
  return {
    id: "weight",
    header: () => <WeightHeaderCell />,
    size: sizeCalculator("weight"),
    cell: (cell) => <WeightCell data={cell.row.original} />,
    meta: {
      align: "right",
    },
  };
};
export const tsmakeWeightColumnImperial = (
  sizeCalculator: (code: string) => number | undefined,
): ColumnDef<TabularViewData> => {
  return {
    id: "weightImperial",
    header: () => <WeightHeaderCell />,
    size: sizeCalculator("weightImperial"),
    cell: (cell) => <WeightCell data={cell.row.original} />,
    meta: {
      align: "right",
    },
  };
};

export const tsmakeCompareColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "compare",
    header: () => <ProductComparisonHeaderCell />,
    cell: (cell) => <ComparisonCell data={cell.row.original} />,
    size: 106,
    meta: {},
  };
};

export const ACTIONS_COL_WIDTH = 38 * 4; // 4 actions;

export const tsmakeActionsColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "actions",
    header: "",
    cell: (cell) => <ProductTabularViewActionCell productCode={cell.row.original.product.code} />,
    size: ACTIONS_COL_WIDTH,
    meta: {
      columnType: "action",
      className: "scha__tabular-view__action-cell-bg",
      align: "right",
    },
  };
};
export const tsmakeHSAActionsColumn = (): ColumnDef<TabularViewData> => {
  return {
    id: "actions",
    header: "",
    cell: (cell) => {
      return <HousingProductTabularViewActionCell row={cell.row} />;
    },
    size: ACTIONS_COL_WIDTH,
    meta: {
      columnType: "action",
      align: "right",
      getBodyCellClasses: (row) =>
        row.original.variantProducts.length > 0
          ? "scha__tabular-view__hsa-action-cell-bg"
          : "scha__tabular-view__action-cell-bg",
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const calculateTotalTableWidth = (columnConfig: ColumnDef<any>[]) => {
  const sizes = columnConfig.map((c) => c.size).filter(isNonNull);
  if (sizes.length > 0 && sizes.some((s) => s < 1)) {
    throw new Error("Not implemented");
  }
  return sizes.reduce((a, b) => a + b, 0);
};
